import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { api } from "../../../lib/api";
import { apiErr } from "../../../lib/apiErr";
import { Chip, ROLES, ROLE_TONE, SCOPES, fmtDate } from "./skillUtil";

const STAGE = { ask: ["Tanya", "cyan"], ask_free: ["Tanya bebas", "cyan"], summary: ["Ringkasan", "brand"], card_open: ["Kartu dibuka", "amber"], card_fix: ["Kartu diperbaiki", "amber"], card: ["Kartu", "green"] };
const th = "px-3 py-2.5";

function Summary({ d }) {
  const cards = [["card_turn", "Kartu ringkasan di giliran", d.card_turn ?? "—"], ["questions", "Pertanyaan sebelum kartu", d.questions_before_card ?? "—"], ["skills", "Skill dipakai", (d.items || []).length], ["turns", "Jejak intake", (d.intake_trace || []).length]];
  return <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{cards.map(([k, l, v]) => <div key={k} className="aivora-card p-4" data-testid={`cu-sum-${k}`}><p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{l}</p><p className="mt-1 font-mono text-2xl font-bold text-slate-900" data-testid={`cu-sum-${k}-value`}>{v}</p></div>)}</div>;
}

function Items({ items }) {
  return (
    <div className="aivora-card overflow-x-auto p-0" data-testid="cu-items">
      <p className="px-4 pt-4 text-sm font-bold text-slate-900">Skill yang dipakai</p>
      <table className="mt-2 w-full min-w-[640px] text-left text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>{["Skill", "Versi", "Role", "Cakupan", "Hasil", "Waktu (WIB)"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-line">{items.map((u, i) => (
          <tr key={i} data-testid={`cu-item-${i}`}>
            <td className={th}><Link to={`/skills/${u.skill_id}`} className="font-mono font-bold text-slate-800 hover:text-brand">{u.skill_id}</Link></td>
            <td className={`${th} font-mono`}>v{u.version ?? "—"}</td>
            <td className={th}>{u.role ? <Chip tone={ROLE_TONE[u.role] || "slate"}>{ROLES[u.role] || u.role}</Chip> : "—"}</td>
            <td className={th}>{SCOPES[u.scope] || u.scope || "—"}</td>
            <td className={th}>{u.outcome || "—"}</td>
            <td className={`${th} whitespace-nowrap text-slate-500`}>{fmtDate(u.at || u.created_at)}</td>
          </tr>))}
          {!items.length && <tr><td colSpan={6} className="px-4 py-5 text-center text-slate-400">Tidak ada baris pemakaian.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Trace({ trace, cardTurn }) {
  return (
    <div className="aivora-card overflow-x-auto p-0" data-testid="cu-trace">
      <p className="px-4 pt-4 text-sm font-bold text-slate-900">Jejak intake per giliran</p>
      <table className="mt-2 w-full min-w-[560px] text-left text-xs">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr>{["Giliran", "Tahap", "Skill", "Versi", "Waktu (WIB)"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-line">{trace.map((t, i) => { const [l, tone] = STAGE[t.stage] || [t.stage, "slate"]; return (
          <tr key={i} className={t.turn === cardTurn ? "bg-emerald-50/60" : ""} data-testid={`cu-trace-${i}`}>
            <td className={`${th} font-mono font-bold`}>{t.turn}</td><td className={th}><Chip tone={tone} testid={`cu-trace-${i}-stage`}>{l}</Chip></td>
            <td className={`${th} font-mono`}>{t.skill_id || "—"}</td><td className={`${th} font-mono`}>{t.version != null ? `v${t.version}` : "—"}</td>
            <td className={`${th} whitespace-nowrap text-slate-500`}>{fmtDate(t.at)}</td>
          </tr>); })}
          {!trace.length && <tr><td colSpan={5} className="px-4 py-5 text-center text-slate-400">Tidak ada jejak intake.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function ConversationUsage() {
  const [cid, setCid] = useState(""); const [d, setD] = useState(null); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const find = async (e) => {
    e.preventDefault(); if (!cid.trim()) return; setBusy(true); setErr("");
    try { setD((await api.get("/platform/skills/usage", { params: { conversation_id: cid.trim() } })).data); } catch (x) { setD(null); setErr(x?.response?.status === 404 ? "Belum ada jejak untuk percakapan ini." : apiErr(x, "Gagal memuat penggunaan skill")); } finally { setBusy(false); }
  };
  const empty = d && !(d.items || []).length && !(d.intake_trace || []).length;
  return (
    <div className="space-y-5" data-testid="cu-tab">
      <form onSubmit={find} className="aivora-card flex flex-wrap items-center gap-2 p-4">
        <input value={cid} onChange={(e) => setCid(e.target.value)} placeholder="ID percakapan, mis. conv_8f2a…" className="input-ui min-w-0 flex-1 font-mono text-xs" data-testid="cu-input" />
        <button type="submit" disabled={busy || !cid.trim()} className="btn-primary rounded-xl px-5 py-2 text-xs" data-testid="cu-search">{busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Cari</button>
        <p className="basis-full text-[11px] text-slate-500">Hanya data penggunaan skill — isi pesan tidak ditampilkan.</p>
      </form>
      {err && <p className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700" data-testid="cu-error">{err}</p>}
      {empty && <p className="aivora-card p-8 text-center text-sm text-slate-400" data-testid="cu-empty">Belum ada jejak untuk percakapan ini.</p>}
      {d && !empty && <><Summary d={d} /><Items items={d.items || []} /><Trace trace={d.intake_trace || []} cardTurn={d.card_turn} /></>}
    </div>
  );
}
